import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, effect, EventEmitter, Input, Output, signal } from '@angular/core';
import { ActionRole, ButtonType } from '@newroom-connect/library/enums';
import { ObjectHelper } from '@newroom-connect/library/helpers';
import { IActionButtonEvent, IListItemClickedEvent, ListItemType, IListConfiguration, IListItem } from '@newroom-connect/library/interfaces';
import { ExtractPropertyPipe, ToStringPipe } from '@newroom-connect/library/pipes';
import { InfiniteScrollDirective } from '@newroom-connect/library/directives';

import { ButtonComponent } from '../buttons/button/button.component';
import { SearchComponent } from '../search/search.component';
import { BaseListComponent } from '../list/abstract/base-list.component';
import { IconComponent } from '../icon/icon.component';

import { CollectionPermissionListItemComponent } from './collection-permission-list-item/collection-permission-list-item.component';

export interface ICollectionPermission {
  id: string;
  access: 'ALLOW' | 'DENY';
}

export interface IGroupedItems {
  groupKey: string;
  groupLabel: string;
  items: IListItem[];
  isCollapsed: boolean;
  permissions: ICollectionPermission[];
}

@Component({
  standalone: true,
  imports: [
    CommonModule,
    CollectionPermissionListItemComponent,
    ExtractPropertyPipe,
    ToStringPipe,
    ButtonComponent,
    SearchComponent,
    InfiniteScrollDirective,
    IconComponent
  ],
  selector: 'nrc-collection-permission-list',
  templateUrl: './collection-permission-list.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CollectionPermissionListComponent extends BaseListComponent {
  @Input({ required: true }) public config!: IListConfiguration;
  @Input({ required: true }) public permissionType: string | null = null;
  @Input({ required: true }) public collectionName: string | null = null;

  // Convert to signals for proper reactivity
  public allowedRecordIdsSignal = signal<string[]>([]);
  public deniedRecordIdsSignal = signal<string[]>([]);

  @Input()
  public set allowedRecordIds(value: string[]) {
    this.allowedRecordIdsSignal.set(value);
  }

  @Input()
  public set deniedRecordIds(value: string[]) {
    this.deniedRecordIdsSignal.set(value);
  }

  @Output() public rowClickedEvent = new EventEmitter<IListItemClickedEvent>();
  @Output() public itemActionButtonEvent = new EventEmitter<IActionButtonEvent>();
  @Output() public selectionChanged = new EventEmitter<{ allowedIds: string[]; deniedIds: string[] }>();
  @Output() public searchEvent = new EventEmitter<string>();

  public listItemType = ListItemType;
  public actionRole = ActionRole;
  public buttonType = ButtonType;

  private permissionList = signal<ICollectionPermission[]>([]);
  public groupedItems = signal<IGroupedItems[]>([]);

  /**
   * Computed signal that tracks the overall permission state of all items.
   * Returns 'ALLOW' if all items are allowed, 'DENY' if all are denied, or 'MIXED' if there's a mix.
   */
  public overallState = computed<'ALLOW' | 'DENY' | 'MIXED'>(() => {
    const list = this.permissionList();

    if (list.length === 0) {
      return 'ALLOW';
    }

    const allAllow = list.every(permission => permission.access === 'ALLOW');
    const allDeny = list.every(permission => permission.access === 'DENY');

    if (allAllow) {
      return 'ALLOW';
    }

    if (allDeny) {
      return 'DENY';
    }

    return 'MIXED';
  });

  /**
   * Check if grouping is enabled.
   */
  public isGroupingEnabled = computed<boolean>(() => {
    return this.config?.groupingConfig?.enabled ?? false;
  });

  /**
   * @constructor
   */
  constructor() {
    super();

    effect(() => {
      const items = this.items();
      const deniedIds = this.deniedRecordIdsSignal();
      const allowedIds = this.allowedRecordIdsSignal();

      if (!items) {
        return;
      }

      this.permissionList.set(
        items.map(item => {
          const itemId = String(item.value.id);

          // Determine access based on existing permission conditions
          let access: 'ALLOW' | 'DENY' = 'ALLOW';

          // Check if this item is explicitly denied
          if (deniedIds.includes(itemId)) {
            access = 'DENY';
          } else if (allowedIds.includes(itemId)) {
            // Check if this item is explicitly allowed
            access = 'ALLOW';
          }
          // Otherwise default to ALLOW

          // Update the item.value.access for template binding
          item.value.access = access;

          return {
            id: itemId,
            access
          };
        })
      );

      // Build grouped items if grouping is enabled
      if (this.config?.groupingConfig?.enabled) {
        this.buildGroupedItems(items);
      }
    },
    { allowSignalWrites: true }
    );
  }

  /**
   * Build grouped items from flat items list.
   *
   * @param items The flat list of items.
   */
  private buildGroupedItems(items: IListItem[]): void {
    const groupingConfig = this.config.groupingConfig;

    if (!groupingConfig) {
      return;
    }

    const groupByKey = groupingConfig.groupByKey;
    const groupLabelKey = groupingConfig.groupLabelKey || groupByKey;
    const defaultCollapsed = groupingConfig.defaultCollapsed ?? false;

    // Group items by the specified key
    const groupsMap = new Map<string, IListItem[]>();

    for (const item of items) {
      const groupKey = String(ObjectHelper.extractProperty(item.value, groupByKey) ?? 'Ungrouped');
      const groupItems = groupsMap.get(groupKey);

      if (groupsMap.has(groupKey) && groupItems) {
        groupItems.push(item);
        continue;
      }

      groupsMap.set(groupKey, [item]);
    }

    // Convert map to array of grouped items
    const grouped: IGroupedItems[] = Array.from(groupsMap.entries()).map(([groupKey, groupItems]) => {
      // Get group label (might be different from group key)
      const groupLabel = groupItems.length > 0
        ? String(ObjectHelper.extractProperty(groupItems[0].value, groupLabelKey) ?? groupKey)
        : groupKey;

      // Get permissions for this group
      const groupPermissions = groupItems.map(item => {
        const itemId = String(item.value.id);
        const permission = this.permissionList().find(p => p.id === itemId);

        return permission ?? { id: itemId, access: 'ALLOW' as const };
      });

      return {
        groupKey,
        groupLabel,
        items: groupItems,
        isCollapsed: defaultCollapsed,
        permissions: groupPermissions
      };
    });

    // Sort groups if specified
    if (groupingConfig.sortGroups) {
      grouped.sort((a, b) => {
        const comparison = a.groupLabel.localeCompare(b.groupLabel);

        return groupingConfig.sortGroups === 'asc' ? comparison : -comparison;
      });
    }

    this.groupedItems.set(grouped);
  }

  /**
   * Toggles the access.
   *
   * @param item
   */
  public toggleAccess(item: Record<string, unknown>): void {
    if (!item || !item.id) {
      return;
    }

    const itemId = String(item.id);

    this.permissionList.update((list) =>
      list.map((permission) =>
        permission.id === itemId
          ? { ...permission, access: permission.access === 'ALLOW' ? 'DENY' : 'ALLOW' }
          : permission
      )
    );

    const updatedPermission = this.permissionList().find((permission) => permission.id === itemId);

    if (updatedPermission) {
      item.access = updatedPermission.access;
    }

    // Emit selection state after toggling
    this.emitSelectionState();
  }

  /**
   * Emits the current selection state whenever it changes.
   */
  private emitSelectionState(): void {
    const allowedIds = this.permissionList()
      .filter(permission => permission.access === 'ALLOW')
      .map(permission => permission.id);

    const deniedIds = this.permissionList()
      .filter(permission => permission.access === 'DENY')
      .map(permission => permission.id);

    this.selectionChanged.emit({ allowedIds, deniedIds });
  }

  /**
   * Toggles all items between ALLOW and DENY states.
   * Cycles through: MIXED/DENY → ALLOW → DENY → ALLOW.
   */
  public toggleAllAccess(): void {
    const currentState = this.overallState();
    const newAccess: 'ALLOW' | 'DENY' = currentState === 'ALLOW' ? 'DENY' : 'ALLOW';

    this.permissionList.update((list) =>
      list.map((permission) => ({ ...permission, access: newAccess }))
    );

    // Update the items array to reflect the changes.
    const items = this.items();

    if (items) {
      items.forEach(item => {
        item.value.access = newAccess;
      });
    }

    // Emit selection state after toggling all
    this.emitSelectionState();
  }

  /**
   * Toggle collapse state for a specific group.
   *
   * @param groupKey The key of the group to toggle.
   */
  public toggleGroupCollapse(groupKey: string): void {
    this.groupedItems.update(groups =>
      groups.map(group =>
        group.groupKey === groupKey
          ? { ...group, isCollapsed: !group.isCollapsed }
          : group
      )
    );
  }

  /**
   * Get the overall permission state for a group.
   *
   * @param group The group to check.
   * @returns 'ALLOW' if all items are allowed, 'DENY' if all are denied, or 'MIXED' if there's a mix.
   */
  public getGroupState(group: IGroupedItems): 'ALLOW' | 'DENY' | 'MIXED' {
    if (group.permissions.length === 0) {
      return 'ALLOW';
    }

    const allAllow = group.permissions.every(p => p.access === 'ALLOW');
    const allDeny = group.permissions.every(p => p.access === 'DENY');

    if (allAllow) {
      return 'ALLOW';
    }

    if (allDeny) {
      return 'DENY';
    }

    return 'MIXED';
  }

  /**
   * Toggle all items in a specific group between ALLOW and DENY.
   *
   * @param group The group to toggle.
   */
  public toggleGroupAccess(group: IGroupedItems): void {
    const currentState = this.getGroupState(group);
    const newAccess: 'ALLOW' | 'DENY' = currentState === 'ALLOW' ? 'DENY' : 'ALLOW';

    // Update permission list for all items in this group
    const groupItemIds = group.items.map(item => String(item.value.id));

    this.permissionList.update(list =>
      list.map(permission =>
        groupItemIds.includes(permission.id)
          ? { ...permission, access: newAccess }
          : permission
      )
    );

    // Update the items array to reflect the changes
    group.items.forEach(item => {
      item.value.access = newAccess;
    });

    // Rebuild grouped items to update group permissions
    if (this.config?.groupingConfig?.enabled) {
      const items = this.items();

      if (items) {
        this.buildGroupedItems(items);
      }
    }

    // Emit selection state after toggling group
    this.emitSelectionState();
  }

  /**
   * Handle search input from the search component.
   * Emits the search term to the parent component for server-side filtering.
   *
   * @param searchTerm The search term entered by the user.
   */
  public handleSearch(searchTerm: string): void {
    this.searchEvent.emit(searchTerm);
  }
}
